/**
 * This utilities module holds reusable functions for the IEP extraction editor: turning raw
 * extraction JSON keys into human-friendly labels, inferring how each value should be edited, and
 * producing blank templates when a user adds a new item to a section.
 *
 * POC scope: this is intentionally schema-light. We infer everything from the shape of the
 * extraction document itself rather than a hand-maintained schema so the editor stays in sync
 * with whatever the extraction pipeline emits.
 */

import isBoolean from 'lodash/isBoolean'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import omit from 'lodash/omit'

import { PLAN_TYPE_IPP } from '../../arch/iepConstants'

import {
  ACRONYMS,
  ADD_LABELS,
  ALLOWED_SECTION_FIELDS,
  ALWAYS_EXCLUDED_KEYS,
  DATE_KEY,
  EXCLUDED_KEYS_BY_SECTION,
  IPP_ALLOWED_SECTION_FIELDS,
  IPP_REQUIRED_FIELDS,
  IPP_REQUIRED_FIELD_LABELS,
  LONG_TEXT_KEY,
  PARTIAL_DATE,
  REQUIRED_FIELD_LABELS,
  REQUIRED_FIELDS,
  SECTION_TITLES,
  SPLIT_ORDER_KEY,
  SPLIT_SECTIONS,
  TECHNICAL_SECTIONS
} from './constants'

// Returns true if the section key is internal machinery rather than user-facing IEP content.
const isTechnicalSection = key => key.startsWith('_') || TECHNICAL_SECTIONS.has(key)

// Splits combined sections (see SPLIT_SECTIONS) out into their own top-level keys, keyed by a
// discriminant field on each item, dropping the combined key — e.g. accommodationsAndModifications
// becomes separate `accommodations`/`modifications` arrays. IepExtractionEditorDialog applies this
// once, right after loading, so those split-out keys behave exactly like any other plain array
// section (Goals, Services, …) with no special-casing anywhere else in the editor. Only that dialog
// uses this — IepSectionItemEditDrawer keeps working against the combined key as-is.
const splitCombinedSections = doc => {
  if (!doc) {
    return doc
  }
  return Object.entries(SPLIT_SECTIONS).reduce((current, [sourceKey, { field, parts, defaultKey }]) => {
    const sourceList = current[sourceKey]
    if (!Array.isArray(sourceList)) {
      return current
    }

    const splitKeys = {}
    const keysInOrder = [...new Set([...parts.map(({ key }) => key), ...(defaultKey ? [defaultKey] : [])])]
    keysInOrder.forEach(key => {
      splitKeys[key] = []
    })

    // Stamp each item with its original position so mergeCombinedSections can put the array back
    // in document order regardless of which split key it ended up under.
    sourceList.forEach((item, index) => {
      const matchedPart = parts.find(({ value }) => item?.[field] === value)
      const targetKey = matchedPart ? matchedPart.key : defaultKey
      // An item whose `field` value matches no part and there's no defaultKey configured has
      // nowhere defined to go — same as before this function stamped an order key, it's dropped.
      if (targetKey) {
        splitKeys[targetKey].push({ ...item, [SPLIT_ORDER_KEY]: index })
      }
    })

    // Insert the split keys where the combined section used to sit, rather than appending them —
    // otherwise they'd jump to the end of Object.keys(doc), reordering the sidebar.
    const result = {}
    Object.keys(current).forEach(key => {
      if (key === sourceKey) {
        Object.assign(result, splitKeys)
      } else {
        result[key] = current[key]
      }
    })
    return result
  }, doc)
}

// Reverses splitCombinedSections: merges each split section's items back into the one combined key
// the backend expects, restoring the original document order via SPLIT_ORDER_KEY (see
// splitCombinedSections) rather than grouping all of one part before the next. IepExtractionEditorDialog
// applies this right before saving, so the PUT payload always has the shape the backend understands
// regardless of how the editor split it up for display.
const mergeCombinedSections = doc => {
  if (!doc) {
    return doc
  }
  return Object.entries(SPLIT_SECTIONS).reduce((current, [sourceKey, { parts, defaultKey }]) => {
    const partKeys = [...new Set([...parts.map(({ key }) => key), ...(defaultKey ? [defaultKey] : [])])]
    if (!partKeys.some(key => key in current)) {
      return current
    }
    const combined = partKeys
      .flatMap(key => current[key] || [])
      .sort((a, b) => (a?.[SPLIT_ORDER_KEY] ?? Infinity) - (b?.[SPLIT_ORDER_KEY] ?? Infinity))
      .map(item => omit(item, [SPLIT_ORDER_KEY]))

    return { ...omit(current, partKeys), [sourceKey]: combined }
  }, doc)
}

// Accommodations and modifications share a single array in the extraction document (see
// EXTRACTION_SECTION_KEYS in IepSectionItemEditDrawer), distinguished only by each item's own
// `type`. A generic "Add" on this section can't infer which one the user means, so callers must ask.

// Returns true if `key` is one of the required fields for `sectionKey` — or, since a recommendation
// can be nested under any section's item (goals, accommodations, …) and always carries its own
// required field regardless of the outer section, also true if `key` is required for the
// 'recommendations' entry in REQUIRED_FIELDS.
// The goals and services section keys are shared between the IEP and IPP vocabularies, so
// required-field lookups resolve through the IPP tables when the plan is an IPP.
const requiredFieldsFor = (sectionKey, planType) =>
  (planType === PLAN_TYPE_IPP ? IPP_REQUIRED_FIELDS[sectionKey] : undefined) ?? REQUIRED_FIELDS[sectionKey]

const isFieldRequired = (key, sectionKey, planType) =>
  Boolean(sectionKey && requiredFieldsFor(sectionKey, planType)?.has(key)) || REQUIRED_FIELDS.recommendations.has(key)

// True when every recommendation in `recommendations` (if any) has its own required field(s) filled
// in — a goal/service/accommodation/modification can carry a nested `recommendations` array, and
// isRequiredFieldsSatisfied below folds this in so an item can't be saved with an incomplete one.
const areRecommendationsSatisfied = recommendations =>
  !Array.isArray(recommendations) ||
  recommendations.every(recommendation =>
    [...REQUIRED_FIELDS.recommendations].some(key => Boolean(String(recommendation?.[key] ?? '').trim()))
  )

// True when every required field for `sectionKey` has a non-empty value on `item`, and any
// recommendations nested under `item` are themselves complete (see areRecommendationsSatisfied). A
// section listing multiple key names (see REQUIRED_FIELDS) is satisfied if any one of them is
// filled in. IepSectionItemEditDrawer uses this to gate its Save button.
const isRequiredFieldsSatisfied = (item, sectionKey, planType) => {
  const required = sectionKey && requiredFieldsFor(sectionKey, planType)

  const ownFieldsSatisfied =
    !required || required.size === 0
      ? true
      : Boolean(item) && [...required].some(key => Boolean(String(item[key] ?? '').trim()))

  return ownFieldsSatisfied && areRecommendationsSatisfied(item?.recommendations)
}

// Returns the "X is required." hint for a section's primary required field, or '' if none.
const requiredFieldsMessage = (sectionKey, planType) => {
  const label =
    (planType === PLAN_TYPE_IPP ? IPP_REQUIRED_FIELD_LABELS[sectionKey] : undefined) ??
    REQUIRED_FIELD_LABELS[sectionKey]
  return label ? `${label} is required.` : ''
}

// Humanizes a camelCase or snake_case key into a capitalized, readable label.
const humanizeLabel = key => {
  if (SECTION_TITLES[key]) {
    return SECTION_TITLES[key]
  }

  const formatted = String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^_+/, '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => (ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ')

  return formatted
}

// Returns a friendly title for a section, falling back to humanizeLabel if not defined.
const sectionTitle = key => SECTION_TITLES[key] || humanizeLabel(key)

// Returns the singular label used for the "Add" button of an array section.
const addLabel = key => ADD_LABELS[key] || 'Item'

// Generates a stable, DOM-safe ID from a path array for node identification.
const idFor = path => `iep-node-${path.map(part => String(part).replace(/[^a-zA-Z0-9_-]/g, '_')).join('-')}`

// Truncates a string to a maximum length and appends an ellipsis if exceeded.
const truncate = (text, max = 70) => {
  const flat = String(text).replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max)}…` : flat
}

// Generates a descriptive, human-friendly label for a single item inside an array.
const itemLabel = (sectionKey, item, index) => {
  const fallbackLabel = addLabel(sectionKey)
  const fallback = `${fallbackLabel} ${index + 1}`

  if (item === null || item === undefined) {
    return fallback
  }

  if (!isObject(item)) {
    return truncate(item) || fallback
  }

  const candidate =
    item.goalNumber ||
    item.serviceName ||
    item.statementText ||
    item.serviceType ||
    item.planName ||
    item.title ||
    item.areaName ||
    item.contactName ||
    item.listedPerformanceArea ||
    item.text ||
    item.goalText ||
    item.plopText ||
    item.quote

  return candidate ? truncate(candidate) : fallback
}

// Infers the schema type/kind of a field value to determine the appropriate form control.
const inferKind = (key, value, planType) => {
  // serviceType is an array of strings in the raw JSON extraction (loaded by IepExtractionEditorDialog)
  // but may be stored as a plain string in the database IEP records (loaded by IepSectionItemEditDrawer).
  // Force it to 'array' here so it consistently renders as a collapsible list of text fields.
  // IPP service/support items use serviceType as a plain string, so they are exempt.
  if (key === 'serviceType' && planType !== PLAN_TYPE_IPP) {
    return 'array'
  }
  // relatedToTests is a free-text field (e.g. "State assessments") — some extraction data has
  // stored it as a boolean, which would otherwise misrender this as a yes/no switch.
  if (key === 'relatedToTests') {
    return 'text'
  }
  if (isBoolean(value)) {
    return 'boolean'
  }
  if (isNumber(value)) {
    return 'number'
  }
  if (Array.isArray(value)) {
    return 'array'
  }
  if (isObject(value)) {
    return 'object'
  }

  // From here, value is a string or null — both editable as text-like inputs.
  // DATE_KEY is written against snake_case names (e.g. "start_date"); the extraction document is
  // edited in camelCase ("startDate"), which has no underscore for it to match, so normalize
  // before testing.
  const snakeKey = key.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`)
  if (DATE_KEY.test(snakeKey) && (value === null || value === '' || PARTIAL_DATE.test(value))) {
    return 'date'
  }
  if (LONG_TEXT_KEY.test(key) || (isString(value) && value.length > 80)) {
    return 'multiline'
  }
  return 'text'
}

// Fields backed by a non-nullable-in-practice boolean column whose extracted value can still be
// `null` (e.g. the AI extractor couldn't determine it) — blankFromTemplate must not let a null
// sibling collapse these to '' the way it does for genuinely optional text fields, since the
// backend rejects an empty string for a boolean column.
const BOOLEAN_TEMPLATE_KEYS = new Set(['esy'])

// Creates an empty/blank data structure matching the structural template of a sibling item.
const blankFromTemplate = (template, key) => {
  if (Array.isArray(template)) {
    return []
  }
  if (isObject(template)) {
    return Object.keys(template).reduce((blank, fieldKey) => {
      blank[fieldKey] = blankFromTemplate(template[fieldKey], fieldKey)
      return blank
    }, {})
  }
  if (isBoolean(template) || BOOLEAN_TEMPLATE_KEYS.has(key)) {
    return false
  }
  if (isNumber(template)) {
    return null
  }
  // Strings and nulls both start empty.
  return ''
}

// Maps a top-level section key to the allowed fields Set. For the combined
// accommodations_and_modifications section the sub-type on the item itself determines
// which allow-list applies (modification vs. accommodation).
const resolveAllowedFields = (topSection, value, planType) => {
  // The IPP vocabularies override the IEP ones for the shared goals/services keys and supply the
  // IPP-only sections; anything unrecognized falls through to unrestricted.
  if (planType === PLAN_TYPE_IPP) {
    return IPP_ALLOWED_SECTION_FIELDS[topSection] ?? null
  }

  if (topSection === 'goals') return ALLOWED_SECTION_FIELDS.goals
  // 'present_levels' is the extraction document's actual top-level key; 'plops' is the section
  // key IepSectionItemEditDrawer uses for the same section (see SECTION_KEY_PLOPS). Both must
  // resolve to the same allow-list so the drawer and the full-document editor stay in sync.
  if (topSection === 'present_levels' || topSection === 'presentLevels' || topSection === 'plops')
    return ALLOWED_SECTION_FIELDS.plops
  if (topSection === 'services') return ALLOWED_SECTION_FIELDS.services
  if (
    topSection === 'accommodations_and_modifications' ||
    topSection === 'accommodationsAndModifications' ||
    topSection === 'accommodations' ||
    topSection === 'modifications'
  ) {
    const type = value.type || (topSection === 'modifications' ? 'modification' : 'accommodation')
    return type === 'modification' ? ALLOWED_SECTION_FIELDS.modifications : ALLOWED_SECTION_FIELDS.accommodations
  }
  return null
}

// Returns the filtered list of child keys for an object node, honouring the excludeKeys prop
// and the per-section allow-lists that restrict which fields are shown in the form.
const filterChildKeys = (keys, { sectionKey, path, excludeKeys, value, planType }) => {
  let result = keys.filter(k => !ALWAYS_EXCLUDED_KEYS.has(k))

  const { length } = path
  const [firstSegment, secondSegment] = path
  // Determines if the current path represents a top-level section item.
  //   - length === 0: the path is at the item root itself (i.e. editing a single item in the drawer).
  //   - length === 2: the path represents an item inside a section list (e.g. ['goals', 0]).
  const isSectionItem =
    length === 0 || (length === 2 && isNumber(secondSegment) && firstSegment === (sectionKey || firstSegment))

  if (isSectionItem) {
    const topSection = sectionKey || firstSegment
    const baseExclusions = EXCLUDED_KEYS_BY_SECTION[topSection] || []

    result = result.filter(k => !baseExclusions.includes(k))

    if (excludeKeys) {
      result = result.filter(k => !excludeKeys.includes(k))
    }

    const allowed = resolveAllowedFields(topSection, value, planType)
    if (allowed) {
      result = result.filter(k => allowed.has(k))
    }
  }

  return result
}

// Updates a nested property in an object or array immutably, returning a new copy of the container.
const setImmutable = (obj, path, value) => {
  const pathArray = Array.isArray(path)
    ? path
    : path
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .filter(Boolean)

  const update = (current, index) => {
    if (index === pathArray.length) {
      return value
    }
    const key = pathArray[index]
    const nextVal = current ? current[key] : undefined

    let currentClone
    if (Array.isArray(current)) {
      currentClone = [...current]
    } else if (isObject(current)) {
      currentClone = { ...current }
    } else {
      currentClone = isNaN(Number(key)) ? {} : []
    }

    currentClone[key] = update(nextVal, index + 1)
    return currentClone
  }

  return update(obj, 0)
}

// Recursively traverses a value tree to build navigation outline node objects for structural elements.
const buildNode = ({ label, value, path, ancestorIds, labels }) => {
  const id = idFor(path)
  const childAncestors = [...ancestorIds, id]
  const children = []
  const isContainerObj = val => isObject(val)

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (isContainerObj(item)) {
        const childLabel = itemLabel(path[path.length - 1], item, index)
        children.push(
          buildNode({
            label: childLabel,
            value: item,
            path: [...path, index],
            ancestorIds: childAncestors,
            labels: [...labels, childLabel]
          })
        )
      }
    })
  } else if (isObject(value)) {
    Object.keys(value).forEach(childKey => {
      if (isContainerObj(value[childKey])) {
        const childLabel = humanizeLabel(childKey)
        children.push(
          buildNode({
            label: childLabel,
            value: value[childKey],
            path: [...path, childKey],
            ancestorIds: childAncestors,
            labels: [...labels, childLabel]
          })
        )
      }
    })
  }

  return { id, label, ancestorIds, labels, children }
}

// Builds the full navigation quick-links outline tree for a given section key and its current value.
const buildSectionOutline = (sectionKey, value) => {
  const label = sectionTitle(sectionKey)
  return buildNode({ label, value, path: [sectionKey], ancestorIds: [], labels: [label] })
}

// Minimum editable fields for IPP items, keyed by section. Priorities, outcomes, and goals share
// the priority/outcome/goal shape; services and supports share theirs. See the IPP tables in
// ./constants for the corresponding allow-lists.
const IPP_DEFAULT_POG_KEYS = {
  domain: '',
  statementText: '',
  goalText: '',
  measurementApproach: '',
  responsibleParty: '',
  targetDate: '',
  pageNumber: null,
  sourceQuote: ''
}

const IPP_DEFAULT_SERVICE_SUPPORT_KEYS = {
  serviceType: '',
  details: '',
  authorizationNumber: '',
  frequency: '',
  fundingSource: '',
  hours: null,
  provider: '',
  startDate: '',
  endDate: '',
  pageNumber: null,
  sourceQuote: ''
}

const IPP_DEFAULT_KEYS_BY_SECTION = {
  priorities: IPP_DEFAULT_POG_KEYS,
  outcomes: IPP_DEFAULT_POG_KEYS,
  goals: IPP_DEFAULT_POG_KEYS,
  services: IPP_DEFAULT_SERVICE_SUPPORT_KEYS,
  supports: IPP_DEFAULT_SERVICE_SUPPORT_KEYS
}

const fillDefaultKeys = (item, sectionKey, planType) => {
  if (!isObject(item)) {
    return item
  }

  const result = { ...item }

  // The goals and services section keys are shared between plan types, so the IPP dispatch comes
  // first — otherwise an IPP goal would pick up the IEP goal vocabulary below.
  if (planType === PLAN_TYPE_IPP) {
    const defaultKeys = IPP_DEFAULT_KEYS_BY_SECTION[sectionKey]

    if (defaultKeys) {
      Object.keys(defaultKeys).forEach(key => {
        if (!(key in result)) {
          result[key] = defaultKeys[key]
        }
      })
    }

    return result
  }

  if (sectionKey === 'goals') {
    const defaultGoalKeys = {
      goalText: '',
      goalType: 'primary',
      baselineText: '',
      listedPerformanceArea: '',
      performanceArea: '',
      category: '',
      measurement: '',
      responsiblePerson: '',
      targetDate: '',
      goalNumber: null,
      recommendations: [],
      secondaryGoals: []
    }

    Object.keys(defaultGoalKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultGoalKeys[key]
      }
    })

    // Top-level items added here are always primary goals (secondary goals/objectives are only
    // ever created via the nested "objectives" sub-form, never as their own array item) — enforce
    // this even when a null-valued sibling caused blankFromTemplate to leave goalType as ''.
    if (!result.goalType) {
      result.goalType = 'primary'
    }

    const gsa = result.goalStructureAssessment || {}
    const defaultGsaKeys = {
      skillDefinition: '',
      supportsDetailing: '',
      timeFrameSpecification: '',
      conditionClarification: '',
      accuracyEvaluationCriteriaOutline: '',
      evaluationMeasurementApproach: '',
      qualityScore: null
    }

    const nextGsa = { ...gsa }
    Object.keys(defaultGsaKeys).forEach(key => {
      if (!(key in nextGsa)) {
        nextGsa[key] = defaultGsaKeys[key]
      }
    })
    result.goalStructureAssessment = nextGsa
  } else if (sectionKey === 'secondaryGoals' || sectionKey === 'secondary_goals') {
    const defaultSecondaryGoalKeys = {
      goalText: '',
      progressReport: {
        annualGoalTargetDate: '',
        annualGoalText: '',
        annualGoalUniqueIdentifier: '',
        id: '',
        pageRefs: [],
        progressComment: '',
        progressReportDate: '',
        progressReportNumber: '',
        progressSummary: '',
        progressText: '',
        reportType: '',
        status: null
      },
      targetDate: '',
      uniqueIdentifier: ''
    }

    Object.keys(defaultSecondaryGoalKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultSecondaryGoalKeys[key]
      }
    })
  } else if (sectionKey === 'services') {
    const defaultServiceKeys = {
      serviceName: '',
      serviceType: [],
      type: '',
      location: '',
      field: '',
      provider: '',
      startDate: '',
      endDate: '',
      durationMinutes: null,
      duration: null,
      frequency: '',
      esy: false,
      percentInGeneralEd: null,
      percentOutsideGeneralEd: null,
      comments: ''
    }

    Object.keys(defaultServiceKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultServiceKeys[key]
      }
    })
  } else if (
    sectionKey === 'accommodations' ||
    sectionKey === 'modifications' ||
    sectionKey === 'accommodations_and_modifications' ||
    sectionKey === 'accommodationsAndModifications'
  ) {
    const defaultAccKeys = {
      type: sectionKey === 'modifications' ? 'modification' : 'accommodation',
      text: '',
      date: '',
      startDate: '',
      endDate: '',
      location: '',
      frequency: '',
      duration: '',
      relatedToTests: '',
      recommendations: []
    }

    Object.keys(defaultAccKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultAccKeys[key]
      }
    })

    // Always enforce the correct type based on the section
    if (sectionKey === 'modifications') {
      result.type = 'modification'
    } else if (sectionKey === 'accommodations') {
      result.type = 'accommodation'
    }
  } else if (sectionKey === 'present_levels' || sectionKey === 'presentLevels' || sectionKey === 'plops') {
    const defaultPlopKeys = {
      title: '',
      category: '',
      text: '',
      plopText: '',
      listedPerformanceArea: '',
      performanceArea: '',
      date: ''
    }

    Object.keys(defaultPlopKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultPlopKeys[key]
      }
    })
  } else if (sectionKey === 'recommendations') {
    const defaultRecommendationKeys = {
      concernText: '',
      title: '',
      type: '',
      description: '',
      details: {}
    }

    Object.keys(defaultRecommendationKeys).forEach(key => {
      if (!(key in result)) {
        result[key] = defaultRecommendationKeys[key]
      }
    })
  }

  return result
}

const createBlankItem = (sectionKey, iep, insertAfterId) => {
  const { planType } = iep ?? {}
  const list = iep?.[sectionKey] || []

  const blankItem = list.length > 0 ? blankFromTemplate(list[0]) : {}

  if (sectionKey === 'accommodations') {
    blankItem.type = 'accommodation'
  } else if (sectionKey === 'modifications') {
    blankItem.type = 'modification'
  }

  delete blankItem.createdAt
  delete blankItem.updatedAt

  // The template came from a *modeled* plan item, which model.ipp stamps with client-side `type`
  // and `number` values that have no place in the extraction document.
  if (planType === PLAN_TYPE_IPP) {
    delete blankItem.type
    delete blankItem.number
  }

  // The backend requires `id` to be populated on create, so a brand-new item is given the same
  // generated UUID for both `id` and `uniqueIdentifier` (the client-safe correlation key used
  // before the item is persisted under a real server-assigned id).
  const uuidVal = crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15)
  blankItem.id = uuidVal
  blankItem.uniqueIdentifier = uuidVal
  blankItem.isNew = true
  if (insertAfterId) {
    blankItem.insertAfterId = insertAfterId

    const insertAfterIndex = list.findIndex(
      candidate =>
        String(candidate?.id) === String(insertAfterId) || String(candidate?.uniqueIdentifier) === String(insertAfterId)
    )
    if (insertAfterIndex !== -1) {
      blankItem.insertAfterIndex = insertAfterIndex
    }
  }

  return fillDefaultKeys(blankItem, sectionKey, planType)
}

export {
  humanizeLabel,
  sectionTitle,
  addLabel,
  itemLabel,
  blankFromTemplate,
  isTechnicalSection,
  idFor,
  inferKind,
  truncate,
  setImmutable,
  filterChildKeys,
  buildSectionOutline,
  createBlankItem,
  isFieldRequired,
  isRequiredFieldsSatisfied,
  requiredFieldsMessage,
  fillDefaultKeys,
  splitCombinedSections,
  mergeCombinedSections
}
